import { Button } from '@/shared/ui/button';
import { ArrowLeft, Check } from 'lucide-react';

/** Les deux offres, dans les termes du document contractuel. Le texte est ici et nulle
 *  part ailleurs : le parcours QR et le parcours installateur doivent poser la MEME
 *  question. Deux formulations donneraient deux consentements differents. */
export const OFFRE_ADHESION = 'A';
export const OFFRE_REFUS = 'B';

const AVANTAGES = [
  'Enregistrement officiel et activation de la garantie constructeur',
  'Alertes en cas de détection de panne, dysfonctionnement ou consommation anormale',
  'Assistance à distance pour identifier les anomalies',
  'Optimisation du fonctionnement des équipements visant à réduire mes consommations',
];

/**
 * Le carrefour de l'offre : adhésion au Pack Connecté & Sérénité, ou refus.
 *
 * Présentationnel, comme les autres étapes du wizard : il ne sait ni enregistrer ni
 * naviguer, il rend le choix.
 *
 * ⚠️ LE PARAPHE A ÉTÉ RETIRÉ LE 09/09/2026, SUR DÉCISION DE BOUBACAR.
 *
 *     « À l'étape 3 du wizard, pas besoin de signature sur canvas. »
 *     « Pour l'adhésion au Pack, pas besoin de signature. On doit juste garder le fait
 *       qu'ils disent oui ou non. »
 *
 * Ce qui disparaît est le PARAPHE, jamais la DÉCISION : le choix reste écrit sur le
 * client et sur le mandat (`offre`), horodaté et attribué au poseur dans l'historique,
 * et le refus garde sa trace propre (`refus_pack`). Un dossier dit donc toujours ce que
 * le client a répondu, et quand.
 *
 * CE QUE LA MESURE DU 09/09 A ÉTABLI AVANT DE RETIRER, parce qu'on ne retire pas une
 * preuve sans savoir ce qui la remplace :
 *
 *     `signature_offre` et `signature_refus` étaient ÉCRITS ET JAMAIS RELUS — aucun PDF,
 *     aucun écran, aucun export, aucun service du dépôt ne les consulte. Le paraphe
 *     dormait en base : récupérable lors d'un litige, invisible partout ailleurs.
 *
 *     ⚠️ Et la signature de fin de chantier NE LE COUVRE PAS. Son libellé énumère trois
 *     actes — « le mandat de flexibilité, les autorisations que je viens de cocher, et
 *     la réception de mon chantier » — et la constante `PORTEE_UNIQUE` les nomme de
 *     même. Le Pack n'y est pas. Le retrait est donc un ARBITRAGE, pris en connaissance
 *     de cause, et non un report sur une autre signature.
 *
 * ⚠️ NE PAS LE REMETTRE « en facultatif ». Un champ de preuve qui existe PARFOIS donne
 * deux dossiers de même nature avec deux valeurs probantes différentes — c'est ce que le
 * retrait du parapheur du CRM (#197) est venu fermer ailleurs le même jour.
 *
 * ⚠️ CET ÉCRAN NE PARLE PAS DE LA SUITE DU PARCOURS. Il annonçait « le mandat de
 * consentement s'applique et sera signé à l'étape suivante » : la phrase promettait une
 * étape qui n'existait nulle part, et le poseur l'attendait devant le client. Retirée le
 * 27/08 sur demande de David. Le mandat se signe désormais à l'étape 4, dans son propre
 * écran — mais c'est cet écran-là qui le dit, pas celui-ci : une étape qui décrit la
 * suivante la promet, et une promesse se retrouve fausse au premier réordonnancement.
 */
export default function StepOffre({ offre, setOffre, setStep, onSuivant, valide, error = '', signing = false }) {
  const choisir = (valeur) => {
    if (valeur === offre) return;
    setOffre(valeur);
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="step-offre">
      <div>
        <h2 className="text-lg font-semibold text-neutral-900">Votre offre</h2>
        <p className="text-[12px] text-neutral-500">Choisissez votre option ci-dessous.</p>
      </div>

      <button
        type="button"
        onClick={() => choisir(OFFRE_ADHESION)}
        data-testid="offre-a"
        aria-pressed={offre === OFFRE_ADHESION}
        className={`w-full text-left rounded-lg border p-4 transition-colors ${
          offre === OFFRE_ADHESION
            ? 'border-marque bg-marque/[0.03]'
            : 'border-neutral-200 bg-white hover:border-neutral-300'
        }`}
      >
        <div className="flex items-center gap-2.5 mb-3">
          <span className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
            offre === OFFRE_ADHESION ? 'border-marque' : 'border-neutral-300'
          }`}>
            {offre === OFFRE_ADHESION && <span className="w-2 h-2 rounded-full bg-marque" />}
          </span>
          <span className="text-sm font-medium text-neutral-900">
            J&apos;adhère au Pack Connecté &amp; Sérénité
          </span>
        </div>
        <ul className="space-y-1.5 pl-6">
          {AVANTAGES.map((a) => (
            <li key={a} className="flex gap-2 text-[12px] text-neutral-600">
              <Check className="w-3.5 h-3.5 shrink-0 mt-0.5 text-neutral-400" />
              <span>{a}</span>
            </li>
          ))}
        </ul>
      </button>

      <button
        type="button"
        onClick={() => choisir(OFFRE_REFUS)}
        data-testid="offre-b"
        aria-pressed={offre === OFFRE_REFUS}
        className={`w-full text-left rounded-lg border p-4 transition-colors ${
          offre === OFFRE_REFUS
            ? 'border-neutral-500 bg-neutral-50'
            : 'border-neutral-200 bg-white hover:border-neutral-300'
        }`}
      >
        <div className="flex items-center gap-2.5 mb-2">
          <span className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
            offre === OFFRE_REFUS ? 'border-neutral-500' : 'border-neutral-300'
          }`}>
            {offre === OFFRE_REFUS && <span className="w-2 h-2 rounded-full bg-neutral-500" />}
          </span>
          <span className="text-sm font-medium text-neutral-700">
            Je n&apos;adhère pas au Pack Connecté &amp; Sérénité
          </span>
        </div>
        <p className="pl-6 text-[12px] text-neutral-500 leading-relaxed">
          Je souhaite uniquement l&apos;enregistrement produit et l&apos;activation de ma
          garantie constructeur, sans services associés.
        </p>
      </button>

      {/* Lot L4 : l'offre refusée envoie le chantier D'ICI. Un refus du serveur (numéro
          déjà rattaché à un autre dossier) doit donc se lire ici, sans quoi « Terminer »
          ne faisait rien en apparence. En adhésion, l'envoi part de l'étape 4 : son
          erreur reste là-bas et ne suit pas le poseur ici. */}
      {error && offre === OFFRE_REFUS && (
        <div className="p-2.5 rounded-md bg-red-50 border border-red-100 text-red-700 text-[12px]"
             role="alert" data-testid="inst-error">{error}</div>
      )}

      <div className="flex gap-2 pt-1">
        <Button variant="outline" className="flex-1 h-10" onClick={() => setStep(2)}
                data-testid="offre-retour">
          <ArrowLeft className="w-4 h-4 mr-1" /> Retour
        </Button>
        <Button className="flex-1 h-10" disabled={!valide || signing} onClick={onSuivant}
                data-testid="offre-suivant">
          {offre === OFFRE_REFUS ? (signing ? 'Envoi…' : 'Terminer le chantier') : 'Suivant'}
        </Button>
      </div>
    </div>
  );
}
